import React, { useEffect, useState } from "react";
import { taskDetailApi } from "../../backend/client";
import {
  TaskDialog,
  TaskDialogActions,
  TaskMessage,
  TaskTextArea,
  TaskTextInput,
} from "./taskFormUi";

const emptyForm = {
  delivery_company: "",
  contact_name: "",
  delivery_date: "",
  delivery_time: "",
  dropoff_address: "",
  box_count: "",
  notes: "",
  reference_number: "",
};

export default function BoxDeliveryForm({
  isOpen,
  onClose,
  projectId,
  taskId,
  onSave,
}: any) {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [form, setForm] = useState(emptyForm);

  const update = (key, value) =>
    setForm((previous) => ({ ...previous, [key]: value }));

  useEffect(() => {
    if (!isOpen || !projectId) return;

    (async () => {
      setLoading(true);
      setErrorMessage("");
      const { data, error } = await taskDetailApi.list("box-deliveries", {
        project_id: projectId,
        ...(taskId ? { task_id: taskId } : {}),
        order: "updated_at",
        ascending: false,
        limit: 1,
      });

      if (!error && data?.[0]) {
        const row = data[0];
        setForm({
          delivery_company: row.delivery_company ?? "",
          contact_name: row.contact_name ?? "",
          delivery_date: row.delivery_date ?? "",
          delivery_time: row.delivery_time ?? "",
          dropoff_address: row.dropoff_address ?? "",
          box_count: row.box_count?.toString?.() ?? "",
          notes: row.notes ?? "",
          reference_number: row.reference_number ?? "",
        });
      } else {
        setForm(emptyForm);
      }
      setLoading(false);
    })();
  }, [isOpen, projectId, taskId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setErrorMessage("");

    const payload = {
      project_id: projectId,
      task_id: taskId ?? null,
      delivery_company: form.delivery_company || null,
      contact_name: form.contact_name || null,
      delivery_date: form.delivery_date || null,
      delivery_time: form.delivery_time || null,
      dropoff_address: form.dropoff_address || null,
      box_count: form.box_count ? Number(form.box_count) : null,
      notes: form.notes || null,
      reference_number: form.reference_number || null,
    };

    const { error } = await taskDetailApi.upsert("box-deliveries", payload);

    setLoading(false);
    if (error) {
      setErrorMessage(error.message || "Failed to save box delivery booking");
      return;
    }

    onSave?.();
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <TaskDialog
      title="Book Box Delivery"
      description="Delivery provider, timing, drop-off address, and booking notes."
      onClose={onClose}
      className="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <TaskMessage>{errorMessage}</TaskMessage>

        <div className="grid gap-4 sm:grid-cols-2">
          <TaskTextInput
            id="box-delivery-company"
            label="Delivery Company"
            value={form.delivery_company}
            onChange={(event) => update("delivery_company", event.target.value)}
            placeholder="Company name"
            required
            className="sm:col-span-2"
          />
          <TaskTextInput
            id="box-contact-name"
            label="Contact Name"
            value={form.contact_name}
            onChange={(event) => update("contact_name", event.target.value)}
            placeholder="Name"
          />
          <TaskTextInput
            id="box-reference"
            label="Reference #"
            value={form.reference_number}
            onChange={(event) => update("reference_number", event.target.value)}
            placeholder="Booking reference"
          />
          <TaskTextInput
            id="box-delivery-date"
            label="Delivery Date"
            type="date"
            value={form.delivery_date}
            onChange={(event) => update("delivery_date", event.target.value)}
            required
          />
          <TaskTextInput
            id="box-delivery-time"
            label="Delivery Time"
            type="time"
            value={form.delivery_time}
            onChange={(event) => update("delivery_time", event.target.value)}
            required
          />
          <TaskTextInput
            id="box-dropoff-address"
            label="Drop-off Address"
            value={form.dropoff_address}
            onChange={(event) => update("dropoff_address", event.target.value)}
            placeholder="Address for delivery"
            required
            className="sm:col-span-2"
          />
          <TaskTextInput
            id="box-count"
            label="Box Count"
            type="number"
            min="0"
            value={form.box_count}
            onChange={(event) => update("box_count", event.target.value)}
            placeholder="15"
          />
          <TaskTextArea
            id="box-notes"
            label="Notes"
            value={form.notes}
            onChange={(event) => update("notes", event.target.value)}
            placeholder="Special instructions..."
            rows={4}
            className="sm:col-span-2"
          />
        </div>

        <TaskDialogActions
          onCancel={onClose}
          submitType="submit"
          disabled={loading}
          submitLabel={loading ? "Saving..." : "Save Booking"}
        />
      </form>
    </TaskDialog>
  );
}
